import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, PageHeader } from "@/components/admin/ui";
import { PageLoader } from "@/components/common";
import { useSettings } from "@/hooks/queries";
import { api, formatApiError } from "@/lib/api";

const Section = ({ title, children }) => (
  <div className="panel space-y-5 p-5 sm:p-6">
    <h3 className="font-display text-xl">{title}</h3>
    {children}
  </div>
);

const BankEditor = ({ accounts, onChange }) => {
  const update = (i, k, v) => onChange(accounts.map((a, j) => (j === i ? { ...a, [k]: v } : a)));
  return (
    <div className="space-y-3">
      {accounts.map((a, i) => (
        <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1.4fr_1.4fr_36px]">
          <Input value={a.bank} onChange={(e) => update(i, "bank", e.target.value)} placeholder="Bank" data-testid={`settings-bank-${i}-bank`} />
          <Input value={a.number} onChange={(e) => update(i, "number", e.target.value)} placeholder="Nomor rekening" data-testid={`settings-bank-${i}-number`} />
          <Input value={a.holder} onChange={(e) => update(i, "holder", e.target.value)} placeholder="Atas nama" data-testid={`settings-bank-${i}-holder`} />
          <Button type="button" size="icon" variant="ghost" onClick={() => onChange(accounts.filter((_, j) => j !== i))} data-testid={`settings-bank-${i}-remove`}><X /></Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...accounts, { bank: "", number: "", holder: "" }])} data-testid="settings-bank-add"><Plus /> Tambah rekening</Button>
    </div>
  );
};

const SettingsForm = ({ initial }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState(initial);
  const ev = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = useMutation({
    mutationFn: (payload) => api.put("/admin/settings", payload),
    onSuccess: () => { toast.success("Pengaturan disimpan"); qc.invalidateQueries({ queryKey: ["settings"] }); qc.invalidateQueries({ queryKey: ["occupancy"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const submit = (e) => { e.preventDefault(); save.mutate({ ...form, dp_percent: Number(form.dp_percent) || 0 }); };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="settings-form">
      <PageHeader title="Pengaturan" subtitle="Info usaha, rekening, dan aturan jadwal">
        <Button type="submit" disabled={save.isPending} data-testid="settings-save-btn">{save.isPending && <Loader2 className="animate-spin" />} Simpan</Button>
      </PageHeader>
      <div className="grid gap-6 xl:grid-cols-2">
        <Section title="Identitas usaha">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nama usaha" className="sm:col-span-2"><Input required value={form.business_name} onChange={ev("business_name")} data-testid="settings-business-name" /></Field>
            <Field label="Tagline" className="sm:col-span-2"><Input value={form.tagline} onChange={ev("tagline")} data-testid="settings-tagline" /></Field>
            <Field label="No. WhatsApp admin" hint="Dipakai untuk tombol konfirmasi"><Input value={form.whatsapp} onChange={ev("whatsapp")} placeholder="08xxxxxxxxxx" data-testid="settings-whatsapp" /></Field>
            <Field label="Instagram"><Input value={form.instagram} onChange={ev("instagram")} placeholder="@siputgunung" data-testid="settings-instagram" /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={ev("email")} data-testid="settings-email" /></Field>
            <Field label="Alamat"><Input value={form.address} onChange={ev("address")} data-testid="settings-address" /></Field>
          </div>
        </Section>
        <Section title="Halaman depan">
          <Field label="Judul utama"><Textarea rows={2} value={form.hero_title} onChange={ev("hero_title")} data-testid="settings-hero-title" /></Field>
          <Field label="Sub judul"><Textarea rows={3} value={form.hero_subtitle} onChange={ev("hero_subtitle")} data-testid="settings-hero-subtitle" /></Field>
        </Section>
        <Section title="Pembayaran">
          <Field label="DP minimal (%)"><Input type="number" min={0} max={100} value={form.dp_percent} onChange={ev("dp_percent")} className="sm:w-32" data-testid="settings-dp-percent" /></Field>
          <Field label="Rekening transfer"><BankEditor accounts={form.bank_accounts} onChange={(v) => setForm((f) => ({ ...f, bank_accounts: v }))} /></Field>
          <Field label="Catatan pembayaran"><Textarea rows={3} value={form.payment_note} onChange={ev("payment_note")} data-testid="settings-payment-note" /></Field>
        </Section>
        <Section title="Aturan jadwal">
          <Field label="Satu tanggal terisi berarti" hint="Pesanan berstatus selain Batal akan menutup tanggal sesuai durasi paket.">
            <Select value={form.schedule_mode} onValueChange={(v) => setForm((f) => ({ ...f, schedule_mode: v }))}>
              <SelectTrigger data-testid="settings-schedule-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="global" data-testid="settings-schedule-mode-global">Tertutup untuk semua trip (1 rombongan per tanggal)</SelectItem>
                <SelectItem value="per_trip" data-testid="settings-schedule-mode-per-trip">Tertutup hanya untuk trip yang sama</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </Section>
      </div>
    </form>
  );
};

export default function Settings() {
  const { data } = useSettings();
  if (!data) return <PageLoader />;
  return <SettingsForm initial={data} />;
}
